import NodeCache from 'node-cache';
import { defineStore } from 'pinia';

import type { AcceptedFileTypes, CoreSiteConfigQuery } from '~/graphql/generated/graphql';

import buildInfo from '@/buildInfo';
import { coreSiteConfig } from '~/graphql/documents/core';
import { ClusterProduct } from '~/graphql/generated/graphql';
import { useClusterRegistryStore } from '~/stores/clusterRegistry';
import UnexpectedGraphqlError from '~/utils/errors/UnexpectedGraphqlError';

// Server-only: NodeCache is a Node lib (in-memory store + a checkperiod timer) and
// loadConfig only runs on the server — the client hydrates from the Pinia payload.
// Gating on import.meta.server keeps node-cache (and a useless browser timer) out of
// the client bundle.
const configCache = import.meta.server ? new NodeCache() : null;
const CONFIG_CACHE_KEY = 'core-site-config-res';

export const useConfigStore = defineStore('config', () => {
  const config = useRuntimeConfig();
  const registry = useClusterRegistryStore();
  const currentCluster = useCurrentCluster();

  const cluster = ref<null | CoreSiteConfigQuery['core']['org']['clusters'][number]>(null);
  const publicConfig = ref<null | CoreSiteConfigQuery['core']['site']['publicSiteConfig']>(null);
  const i18nMessages = ref<null | object>(null);
  const acceptedFileTypes = ref<null | AcceptedFileTypes>(null);

  const hydrateFromQuery = (data: CoreSiteConfigQuery) => {
    registry.setClusters(data.core.org.clusters);

    const code = currentCluster.code.value;
    const matchedCluster = code
      ? data.core.org.clusters.find(c => c.code === code)
      : undefined;
    // A request whose cluster code isn't among the org's clusters is a real
    // inconsistency — fail loudly rather than silently serving clusters[0]
    // (i.e. the wrong tenant's config).
    if (code && !matchedCluster) {
      throw new UnexpectedGraphqlError(`Current cluster "${code}" not found in org clusters`);
    }
    cluster.value = matchedCluster ?? data.core.org.clusters[0] ?? null;

    publicConfig.value = data.core.site.publicSiteConfig;
    i18nMessages.value = JSON.parse(data.core.site.i18nMessages) as object;
    acceptedFileTypes.value = data.core.site.acceptedFileTypes;
  };

  const loadConfig = async () => {
    // Key the cache per cluster: the payload carries locale-specific
    // i18nMessages and region-specific site config, so a single shared key
    // would bleed one cluster's config into another on a shared SSR container.
    const cacheKey = `${CONFIG_CACHE_KEY}:${currentCluster.code.value ?? 'root'}`;
    const cachedConfigData = configCache?.get<CoreSiteConfigQuery>(cacheKey);

    if (config.public.ssrConfigCacheEnabled && cachedConfigData) {
      hydrateFromQuery(cachedConfigData);
      return;
    }

    // Resolved here rather than in the store setup: the store is now created by
    // plugin 00, before 01.urql provides the client, so a setup-time capture
    // would pin `undefined` for the app's lifetime.
    const { $urqlClient } = useNuxtApp();
    const res = await $urqlClient.query(coreSiteConfig, {});

    // A 404 from the API means this host's cluster is not being served at all
    // (e.g. a schools cluster still dark behind its release flag) — answer with
    // the platform 404 page rather than a 500 with internal detail.
    if (res?.error?.response?.status === 404) {
      throw createError({ statusCode: 404, message: 'Page not found' });
    }

    if (!res?.data?.core.org.clusters?.length) {
      throw new UnexpectedGraphqlError('Could not load clusters registry', res.error);
    }

    if (!res.data.core.site.publicSiteConfig) {
      throw new UnexpectedGraphqlError('Could not load public site config', res.error);
    }

    if (!res.data.core.site.i18nMessages) {
      throw new UnexpectedGraphqlError('Could not load i18n messages', res.error);
    }

    if (!res.data.core.site.acceptedFileTypes) {
      throw new UnexpectedGraphqlError('Could not load accepted file types', res.error);
    }

    if (!res.extensions?.build) {
      throw new UnexpectedGraphqlError('Build missing from extensions', res.error);
    }

    // An old app version can still sit behind the load balancer mid-deploy, so
    // only cache when the response build matches this container's build.
    if (config.public.ssrConfigCacheEnabled && res.extensions?.build === buildInfo.build) {
      configCache?.set(cacheKey, res.data, 3600);
    }

    hydrateFromQuery(res.data);
  };

  const isSchoolsCluster = computed(() => cluster.value?.product === ClusterProduct.Schools);

  // Trust the loaded cluster's suppression flag only when it is the cluster this
  // request actually resolved to: hydrateFromQuery above falls back to the org's
  // first cluster when no cluster code is present, and that cluster's flag says
  // nothing about this request. Anything unresolved reads as suppressed.
  const suppressMarketingTechnologies = computed(() => {
    const code = currentCluster.code.value;
    return code !== null && cluster.value?.code === code
      ? cluster.value.suppressMarketingTechnologies
      : true;
  });

  // The cluster every school and school role in this surface's region carries, the same
  // from either host. Used to find the region's schools cluster, by the dashboard-access gate
  // and by the teacher portal's school list.
  const schoolDataClusterId = computed<string | null>(() => cluster.value?.schoolDataClusterId ?? null);

  // Whether this door offers teacher self sign-up. A schools cluster reports its consumer
  // sibling's Nova-set switch (clusters.manage_register_enabled), the same one the
  // signUpTeacher mutation enforces against.
  const teacherRegistrationOpen = computed(() => cluster.value?.teacherRegistrationOpen ?? false);

  return {
    cluster,
    publicConfig,
    i18nMessages,
    acceptedFileTypes,
    isSchoolsCluster,
    suppressMarketingTechnologies,
    schoolDataClusterId,
    teacherRegistrationOpen,
    loadConfig,
  };
});
